import Guard from 'arg-guard'

import Config from './app.config'
import Logger from './logger'

const loggingCategory = 'Rules - PropertyDataRuleManager'

/**
 * Class that is responsible for managing UI behaviour according to data rules.
 *
 * @export PropertyDataRuleManager
 * @class PropertyDataRuleManager
 */
export default class PropertyDataRuleManager {

    /**
     * Creates an instance of PropertyDataRuleManager.
     * @param {Object} referenceDataItems
     * @param {boolean} debug Enable in testing to debug output.
     * @memberof PropertyDataRuleManager
     */
    constructor(referenceDataItems, debug = false) {
        Guard.throwIf(referenceDataItems, 'referenceDataItems')

        this.debug = debug
        this._referenceDataItems = referenceDataItems

        if (Config.verboseRuleLogging)
            Logger.logObject(referenceDataItems, 'PropertyDataRuleManager built with reference data items', loggingCategory)
    }

    /**
     * Valid actions that the UI can perform.
     *
     * @readonly
     * @static
     * @memberof PropertyDataRuleManager
     */
    static get ACTIONS() {
        return {
            show: 'show',
            hide: 'hide',
            none: 'no',
        }
    }

    /**
     * Determines whether an action should be performed and returns the string representation of that action.
     *
     * @param {Array} rules Rules to check.
     * @memberof PropertyDataRuleManager
     */
    getAction(rules) {
        Guard.throwIf(rules, 'rules')

        let actionResult = PropertyDataRuleManager.ACTIONS.none

        rules.forEach(el => {
            const comparisonValue = this.getComparisonValue(el.definition.compareWithDottedString)
            let rulePassed = false
            if (el.definition.type === 'match') {
                rulePassed = this._match(comparisonValue, el.definition.ruleValue1, el.definition.not)
                if (rulePassed) actionResult = el.action
            }
            if (el.definition.type === 'range') {
                rulePassed = this._between(comparisonValue, el.definition.ruleValue1, el.definition.ruleValue2, el.definition.not)
                if (rulePassed) actionResult = el.action
            }
            if (el.definition.type === 'in') {
                rulePassed = this._in(comparisonValue, el.definition.ruleValue1, el.definition.not)
                if (rulePassed) actionResult = el.action
            }

            if (Config.verboseRuleLogging)
                Logger.logObject({ rule: el, type: el.definition.type, action: el.action, comparisonValue, rulePassed })
        })

        this._log({ rules, actionResult }, 'Getting Rule Action Finished')

        return actionResult
    }

    /**
     * Gets the value of the item with the specified identifier.
     *
     * @param {string} identifier Unique element identifier.
     * @memberof PropertyDataRuleManager
     */
    getComparisonValue(identifier) {
        const item = this._find(identifier)
        const value = item ? item.value : '[ No comparison value available ]'

        this._log({ identifier, item, value }, 'Getting Comparison value')
        return value
    }

    /**
     * Determines whether an item should be shown or not, by default it is.
     *
     * @param {Object} item A Segment data item.
     * @returns {boolean} true if the item should be shown, false if not.
     * @memberof PropertyDataRuleManager
     */
    shouldShowItem(item) {
        if (!item) return false
        if (!item.rules || !item.rules.length) return true

        this._log({ item }, 'Determine if item should be shown')

        const hideRules = item.rules.filter(rule => rule.action === PropertyDataRuleManager.ACTIONS.hide)
        const showRules = item.rules.filter(rule => rule.action === PropertyDataRuleManager.ACTIONS.show)
        const result =
              hideRules != null && hideRules.length > 0 && this.getAction(hideRules) !== PropertyDataRuleManager.ACTIONS.hide ||
              showRules != null && showRules.length > 0 && this.getAction(showRules) === PropertyDataRuleManager.ACTIONS.show

        this._log({ hideRules, showRules, result }, 'Associated Rules')
        return result
    }

    /**
     * Updates the internal comparison values state.
     *
     * @param {Object} updatedDataItem An updated data item.
     * @memberof PropertyDataRuleManager
     */
    updateComparisonValuesState(updatedDataItem) {
        const item = this._find(updatedDataItem.dottedString)
        if (item && item.value !== updatedDataItem.value) item.value = updatedDataItem.value
        if (item && item.id !== updatedDataItem.id) item.id = updatedDataItem.id
    }

    /**
     * Finds the item with the matching dotted string for the specified identifier.
     *
     * @param {string} identifier Unique element identifier.
     * @returns {Object} An item from Reference Data Items.
     * @memberof PropertyDataRuleManager
     */
    _find(identifier) {
        this._log({ identifier, dottedStrings: this._referenceDataItems.map(i => {
            return {
                id: i.id,
                dottedString: i.dottedString,
                value: i.value,
                title: i.title,
                type: i.type,
            }
        }) }, 'Search for item', true)

        const item = this._referenceDataItems.find(i => i.dottedString === identifier)

        if (item === undefined || item === null)
            Logger.warn(`The item with dotted string ${ identifier } was not found. Data rules may not be applied correctly.`)

        return item ? item : null
    }

    /**
     * Checks to see if a value is between a range of values.
     *
     * @param {number} comparison The value to compare with.
     * @param {number} value1 The starting value.
     * @param {number} value2 The end value.
     * @param {boolean} [invert=false] A flag to indicate whether to invert the result to 'outside'
     * @memberof PropertyDataRuleManager
     */
    _between(comparison, value1, value2, invert = false) {
        const result = comparison >= value1 && comparison <= value2
        return invert? !result : result
    }

    /**
     * Checks to see if the comparison value is in an array of items.
     *
     * @param {Any} comparison The value to compare with.
     * @param {Array} list The list.
     * @param {boolean} [invert=false] A flag to indicate whether to invert the result to 'not in'
     * @memberof PropertyDataRuleManager
     */
    _in(comparison, list, invert = false) {
        const result = list.includes(comparison)
        return invert? !result : result
    }

    /**
     * Matches a value with a comparative value.
     *
     * @param {Any} comparison The value to compare with.
     * @param {Any} value The value.
     * @param {boolean} [invert=false] A flag to indicate whether to invert the result to 'does not match'
     * @memberof PropertyDataRuleManager
     */
    _match(comparison, value, invert = false) {
        const result = value === comparison
        return invert? !result : result
    }

    /**
     * Logs debug and / or verbose logging messages.
     *
     * @param {Object} objectVar The object data to log.
     * @param {string} message The message
     * @param {boolean} [debugOnly=false] If set to true, this log only logs when debug is set to true.
     * @memberof PropertyDataRuleManager
     */
    /*eslint-disable no-console */
    _log(objectVar, message, debugOnly = false) {
        if (this.debug)
            console.log(JSON.stringify(objectVar, null, 2), message)

        if (Config.verboseRuleLogging && !debugOnly)
            Logger.logObject(objectVar, message, loggingCategory)
    }
    /*eslint-enable no-console */
}
